import {
  type CommunityUserRole,
  type GetCommunityOutput,
  type GetCommunityUserOutput,
  type ListCommunityUserOutput,
} from '@sunrise-ui/api/cerebro';
import type {
  CommunityOrganizerOutput,
  CreateSubcommunityOutput,
  JoinCommunityInput,
  MemberOutput,
  RemoveSubcommunityOutput,
  UpdateMemberOrganizerStatusOutput,
} from '@sunrise-ui/api/network';
import type {
  Community,
  CommunitySize,
  CommunityType,
  CommunityUpdatedOutput,
  CreateCommunityMetaInput,
  FundingSource,
  OrmPaginatorCommunity,
  UpdateCommunityInput,
  UpdateCommunityLinksInput,
} from '@sunrise-ui/api/profile';
import { useCacheAvailability, useSnackbar } from '@sunrise-ui/primitives';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { QueryKeys } from '../constants/QueryKeys';
import {
  assertParameterIsNotEmptyString,
  assertParameterIsNotUndefinedOrNull,
  slugify,
} from '../utils';
import {
  cerebroCommunitiesApi,
  communitiesApi,
  membershipsApi,
  refreshToken,
  subcommunitiesApi,
  useAuthInfiniteQuery,
  useAuthQuery,
} from '../utils/api';

const {
  communitiesKey,
  searchKey,
  byIdKey,
  parentsKey,
  childrenKey,
  membersKey,
  organizersKey,
} = QueryKeys.communities;

/**
 * Retry a function with exponential backoff, only retrying on 404 errors.
 * Used when Profile service may not have synced data from Cerebro yet.
 */
const retryOn404 = async <T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 500,
): Promise<T> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      // Only retry on 404 errors (resource not yet available due to sync delay)
      const is404 = isAxiosError(error) && error.response?.status === 404;

      if (!is404 || attempt === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: 500ms, 1000ms, 2000ms...
      const delay = baseDelayMs * Math.pow(2, attempt);
      console.warn(
        `[retryOn404] Attempt ${attempt + 1} failed with 404, retrying in ${delay}ms...`,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
};

export const useGetCommunities = (limit = 50, offset = 0) =>
  useQuery({
    queryKey: [communitiesKey, limit, offset],
    queryFn: async (): Promise<OrmPaginatorCommunity> => {
      const response = await communitiesApi.getCommunitiesCommunitiesGet({
        limit,
        offset,
      });
      return response.data;
    },
    staleTime: 300000, // 5 minutes
  });

export const useGetCommunitiesInfinite = (limit = 20) => {
  const queryClient = useQueryClient();

  return useAuthInfiniteQuery({
    queryKey: [communitiesKey, limit],
    queryFn: async ({ pageParam }): Promise<OrmPaginatorCommunity> => {
      const response = await communitiesApi.getCommunitiesCommunitiesGet({
        limit,
        offset: pageParam,
      });

      // Cache each community individually so child components can use cached data
      for (const community of response.data.results ?? []) {
        if (community.id) {
          queryClient.setQueryData(
            [communitiesKey, byIdKey, community.id],
            community,
          );
        }
      }

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam(lastPage): number | undefined {
      if (lastPage?.next) return lastPage.offset + lastPage.limit;
    },
    getPreviousPageParam(lastPage): number | undefined {
      if (lastPage?.previous) return lastPage.offset - lastPage.limit;
    },
    staleTime: 300000, // 5 minutes
  });
};

// Memberships (Network): Update organizer status for a specific member
export const useUpdateMemberOrganizerStatus = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    UpdateMemberOrganizerStatusOutput,
    unknown,
    { communityId: string; userId: string; isOrganizer: boolean }
  >({
    async mutationFn({ communityId, userId, isOrganizer }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');
      const response =
        await membershipsApi.updateMemberOrganizerStatusMembershipsCommunitiesCommunityIdMembersUserIdOrganizerStatusPatch(
          {
            communityId,
            userId,
            updateMemberOrganizerStatusInput: { is_organizer: isOrganizer },
          },
        );
      return response.data;
    },
    async onSuccess(_data, { communityId, isOrganizer }) {
      // Invalidate all members queries for this community (any isActive variant)
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, membersKey, communityId],
        refetchType: 'active',
      });
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, organizersKey, communityId],
      });
      showSnackbar({
        text: isOrganizer
          ? 'Organizer access granted'
          : 'Organizer access removed',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update organizer status. Please try again.',
        type: 'error',
      });
    },
  });
};

// Memberships (Network): Organizers of a community (public endpoint)
export const useGetCommunityOrganizers = (communityId?: string) =>
  useQuery({
    queryKey: [communitiesKey, organizersKey, communityId],
    queryFn: async (): Promise<CommunityOrganizerOutput[]> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await membershipsApi.getCommunityOrganizersMembershipsCommunitiesCommunityIdOrganizersGet(
          {
            communityId: communityId as string,
          },
        );
      return response.data ?? [];
    },
    enabled: Boolean(communityId),
    staleTime: 300000, // 5 minutes
  });

// Orchestrated flow: Add a member to a community across services
// Add community member using profile service only (no cerebro)
// 1) Join community in Network (profile service)
// 2) Optionally mark as organizer in Network
export const useAddCommunityMember = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    { userId: string },
    unknown,
    { communityId: string; userId: string; isOrganizer?: boolean }
  >({
    async mutationFn({ communityId, userId, isOrganizer }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');

      // 1) Join community via profile service (Network)
      const joinCommunityInput: JoinCommunityInput = {
        community_id: communityId,
        user_id: userId,
      };
      await membershipsApi.joinCommunityMembershipsJoinPost({
        joinCommunityInput,
      });

      // 2) Set organizer status (defaults to false if not specified)
      await membershipsApi.updateMemberOrganizerStatusMembershipsCommunitiesCommunityIdMembersUserIdOrganizerStatusPatch(
        {
          communityId,
          userId,
          updateMemberOrganizerStatusInput: {
            is_organizer: isOrganizer || false,
          },
        },
      );

      return { userId };
    },
    async onSuccess(_data, { communityId, isOrganizer }) {
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, membersKey, communityId],
      });
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, 'users', communityId],
      });
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, organizersKey, communityId],
      });
      showSnackbar({
        text: isOrganizer ? 'Organizer added' : 'Member added',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({ text: 'Failed to add member', type: 'error' });
    },
  });
};

// Subcommunities: Parents of a given community (treat given community as child)
export const useGetParentCommunityIds = ({
  communityId,
  isActive = true,
}: {
  communityId?: string;
  isActive?: boolean;
}) => {
  const { isQueryKeyAvailable } = useCacheAvailability();
  return useQuery({
    queryKey: [communitiesKey, parentsKey, communityId, isActive],
    enabled:
      Boolean(communityId) &&
      !isQueryKeyAvailable([communitiesKey, parentsKey, isActive]),
    queryFn: async (): Promise<string[]> => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      const response =
        await subcommunitiesApi.getParentCommunitiesSubcommunitiesParentsCommunityIdGet(
          {
            communityId: communityId,
            isActive,
          },
        );

      return response.data.map(
        (parentCommunity) => parentCommunity.parent_community_id,
      );
    },
  });
};

// Bulk Parent Communities: Get parent communities for multiple child communities
// Batches requests in chunks of 100 to comply with API limits
export const useBulkGetParentCommunityIds = ({
  communityIds,
  isActive = true,
  enabled = true,
}: {
  communityIds?: string[];
  isActive?: boolean;
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [communitiesKey, parentsKey, 'bulk', communityIds],
    enabled: enabled && Boolean(communityIds?.length),
    queryFn: async (): Promise<{ [p: string]: string[] }> => {
      assertParameterIsNotUndefinedOrNull(communityIds, 'communityIds');

      // Batch into chunks of 100 to comply with API limit
      const BATCH_SIZE = 100;
      const batches: string[][] = [];
      for (let i = 0; i < communityIds.length; i += BATCH_SIZE) {
        batches.push(communityIds.slice(i, i + BATCH_SIZE));
      }

      // Fetch all batches in parallel
      const batchResults = await Promise.all(
        batches.map((batch) =>
          subcommunitiesApi
            .bulkGetParentCommunitiesSubcommunitiesBulkParentCommunitiesPost({
              bulkGetParentCommunitiesInput: {
                child_community_ids: batch,
                is_active: isActive,
              },
            })
            .then((response) => response.data.parent_communities),
        ),
      );

      // Merge all batch results into a single object
      const data = batchResults.reduce(
        (acc, batchData) => ({ ...acc, ...batchData }),
        {} as { [p: string]: string[] },
      );

      // Cache individual community parent relationships
      for (const [communityId, parentCommunityIds] of Object.entries(data)) {
        queryClient.setQueryData(
          [communitiesKey, parentsKey, communityId, isActive],
          parentCommunityIds,
        );
      }

      return data;
    },
    staleTime: 300000,
  });
};

// Subcommunities: Children of a given parent community
export const useGetSubcommunityIds = (
  communityId?: string,
  isActive: boolean = true,
) => {
  const { isQueryKeyAvailable } = useCacheAvailability();
  return useQuery({
    queryKey: [communitiesKey, childrenKey, communityId, isActive],
    enabled:
      Boolean(communityId) &&
      !isQueryKeyAvailable([communitiesKey, childrenKey, isActive]),
    queryFn: async (): Promise<string[]> => {
      assertParameterIsNotUndefinedOrNull(communityId, 'parentCommunityId');
      const response =
        await subcommunitiesApi.getSubcommunitiesSubcommunitiesCommunitiesParentCommunityIdGet(
          {
            parentCommunityId: communityId,
            isActive,
          },
        );
      return response.data.map((community) => community.child_community_id);
    },
    staleTime: 300000,
  });
};

// Create relation: make selected parent a parent of selected child
export const useCreateSubcommunity = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    CreateSubcommunityOutput,
    unknown,
    { childCommunityId: string; parentCommunityId: string }
  >({
    async mutationFn({ childCommunityId, parentCommunityId }) {
      assertParameterIsNotEmptyString(childCommunityId, 'childCommunityId');
      assertParameterIsNotEmptyString(parentCommunityId, 'parentCommunityId');
      const response =
        await subcommunitiesApi.createSubcommunitySubcommunitiesCreateCommunityIdPost(
          {
            communityId: childCommunityId,
            createSubcommunityInput: { community_id: parentCommunityId },
          },
        );
      return response.data;
    },
    async onSuccess(_data, { childCommunityId, parentCommunityId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, parentsKey, childCommunityId],
        }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, childrenKey, parentCommunityId],
        }),
      ]);
      showSnackbar({ text: 'Relationship saved', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to save relationship. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useRemoveParentFromChild = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    RemoveSubcommunityOutput,
    unknown,
    { parentCommunityId: string; childCommunityId: string }
  >({
    async mutationFn({ parentCommunityId, childCommunityId }) {
      assertParameterIsNotEmptyString(parentCommunityId, 'parentCommunityId');
      assertParameterIsNotEmptyString(childCommunityId, 'childCommunityId');
      const response =
        await subcommunitiesApi.removeParentFromChildSubcommunitiesRemoveParentsCommunityIdChildrenChildCommunityIdDelete(
          {
            communityId: parentCommunityId,
            childCommunityId,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { parentCommunityId, childCommunityId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, parentsKey, childCommunityId],
        }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, childrenKey, parentCommunityId],
        }),
      ]);
      showSnackbar({ text: 'Removed parent', type: 'success' });
    },
    async onError(error: unknown) {
      let message = 'Failed to remove parent';
      if (isAxiosError(error) && error.response?.data?.detail) {
        message = error.response.data.detail;
      }
      showSnackbar({ text: message, type: 'error' });
    },
  });
};

export const useRemoveChildFromParent = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    RemoveSubcommunityOutput,
    unknown,
    { parentCommunityId: string; childCommunityId: string }
  >({
    async mutationFn({ parentCommunityId, childCommunityId }) {
      assertParameterIsNotEmptyString(parentCommunityId, 'parentCommunityId');
      assertParameterIsNotEmptyString(childCommunityId, 'childCommunityId');
      const response =
        await subcommunitiesApi.removeChildFromParentSubcommunitiesRemoveChildrenCommunityIdParentsParentCommunityIdDelete(
          {
            communityId: childCommunityId,
            parentCommunityId,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { parentCommunityId, childCommunityId }) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, childrenKey, parentCommunityId],
        }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, parentsKey, childCommunityId],
        }),
      ]);
      showSnackbar({ text: 'Removed child', type: 'success' });
    },
    async onError() {
      showSnackbar({ text: 'Failed to remove child', type: 'error' });
    },
  });
};

export const useBulkGetCommunities = ({
  communityIds,
  enabled = true,
}: {
  communityIds?: string[];
  enabled?: boolean;
}) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [communitiesKey, 'bulk', communityIds],
    enabled: Boolean(enabled && communityIds && communityIds.length > 0),
    queryFn: async (): Promise<Community[]> => {
      assertParameterIsNotUndefinedOrNull(communityIds, 'communityIds');

      // Batch into chunks of 100 to comply with API limits
      const BATCH_SIZE = 100;
      const batches: string[][] = [];
      for (let i = 0; i < communityIds.length; i += BATCH_SIZE) {
        batches.push(communityIds.slice(i, i + BATCH_SIZE));
      }

      // Fetch all batches in parallel
      const batchResults = await Promise.all(
        batches.map((batch) =>
          communitiesApi
            .bulkGetCommunitiesCommunitiesBulkPost({
              bulkGetCommunitiesInput: { community_ids: batch },
            })
            .then((response) => response.data),
        ),
      );

      // Merge all batch results into a single array
      const data = batchResults.flat();

      // Cache individual communities
      for (const community of data) {
        queryClient.setQueryData(
          [communitiesKey, byIdKey, community.id],
          community,
        );
      }
      return data;
    },
    staleTime: 300000,
  });
};

export const useUpdateCommunity = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    CommunityUpdatedOutput,
    unknown,
    { communityId: string; input: UpdateCommunityInput }
  >({
    async mutationFn({ communityId, input }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await communitiesApi.updateCommunityCommunitiesCommunityIdPut({
          communityId,
          updateCommunityInput: input,
        });
      return response.data;
    },
    async onSuccess(_data, { communityId }) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [communitiesKey] }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, byIdKey, communityId],
        }),
      ]);
      showSnackbar({ text: 'Community updated', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update community. Please try again.',
        type: 'error',
      });
    },
  });
};

// Update the community's photo (headshot) in the Profiles service
export const useUpdateCommunityPhoto = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<unknown, unknown, { communityId: string; file: File }>({
    async mutationFn({ communityId, file }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await communitiesApi.updateCommunityPhotoCommunitiesCommunityIdPhotoPut(
          {
            communityId,
            file,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { communityId }) {
      // Invalidate community-specific caches for photo updates
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [communitiesKey] }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, byIdKey, communityId],
        }),
      ]);

      // Force refetch of the specific community to ensure fresh data
      await queryClient.refetchQueries({
        queryKey: [communitiesKey, byIdKey, communityId],
      });

      showSnackbar({ text: 'Photo updated', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to upload photo. Please try again.',
        type: 'error',
      });
    },
  });
};

// Update community links (social links)
export const useUpdateCommunityLinks = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    unknown,
    unknown,
    { communityId: string; input: UpdateCommunityLinksInput }
  >({
    async mutationFn({ communityId, input }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await communitiesApi.updateCommunityLinksCommunitiesCommunityIdLinksPut(
          {
            communityId,
            updateCommunityLinksInput: input,
          },
        );
      return response.data;
    },
    async onSuccess(_data, { communityId }) {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [communitiesKey] }),
        queryClient.invalidateQueries({
          queryKey: [communitiesKey, byIdKey, communityId],
        }),
      ]);
      showSnackbar({ text: 'Links updated', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update links. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useSearchCommunities = (query?: string, limit = 20) =>
  useAuthInfiniteQuery<
    { data: OrmPaginatorCommunity; query: string },
    unknown,
    { data: OrmPaginatorCommunity; query: string },
    [string, string, string | undefined],
    number
  >({
    queryKey: [communitiesKey, searchKey, query],
    initialPageParam: 0,
    enabled: Boolean(query && query.length > 2),
    queryFn: async ({ pageParam }) => {
      assertParameterIsNotUndefinedOrNull(query, 'query');
      const response =
        await communitiesApi.searchCommunitiesCommunitiesSearchGet({
          query: query as string,
          limit,
          offset: pageParam,
        });
      return { data: response.data, query: query as string };
    },
    getNextPageParam: (lastPage) =>
      lastPage?.data?.next
        ? lastPage.data.offset + lastPage.data.limit
        : undefined,
    getPreviousPageParam: (lastPage) =>
      lastPage?.data?.previous
        ? lastPage.data.offset - lastPage.data.limit
        : undefined,
  });

export const getCommunity = async (communityId: string) => {
  const response = await communitiesApi.getCommunityCommunitiesCommunityIdGet({
    communityId: communityId,
  });
  return response.data;
};

export const useGetCommunity = (communityId?: string) => {
  const { isQueryKeyAvailable } = useCacheAvailability();

  return useQuery({
    queryKey: [communitiesKey, byIdKey, communityId],
    enabled:
      Boolean(communityId) && !isQueryKeyAvailable([communitiesKey, byIdKey]),
    queryFn: async (): Promise<Community> => {
      assertParameterIsNotUndefinedOrNull(communityId, 'communityId');
      return getCommunity(communityId);
    },
  });
};

export const useGetCommunityBySlug = (slug?: string) =>
  useQuery({
    queryKey: [communitiesKey, 'bySlug', slug],
    enabled: Boolean(slug),
    queryFn: async (): Promise<Community> => {
      assertParameterIsNotUndefinedOrNull(slug, 'slug');
      assertParameterIsNotEmptyString(slug, 'slug');
      const response =
        await communitiesApi.getCommunityBySlugCommunitiesSlugSlugGet({
          slug: slug as string,
        });
      return response.data;
    },
    staleTime: 300000,
  });

export const useCreateCommunity = (options?: {
  refreshAfterSuccess?: boolean;
}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const { refreshAfterSuccess = true } = options ?? {};

  return useMutation<
    GetCommunityOutput,
    unknown,
    {
      name: string;
      slug?: string;
      headline?: string;
      bio?: string;
      community_type?: CommunityType;
    }
  >({
    async mutationFn({ name, slug }) {
      const response =
        await cerebroCommunitiesApi.createCommunityCommunitiesPost({
          createCommunityInput: {
            name,
            slug: slugify(slug ?? name),
          },
        });
      return response.data;
    },
    async onSuccess(data, variables) {
      // Optionally refresh token so the new community_id claim is present for subsequent requests
      if (refreshAfterSuccess) {
        await refreshToken().catch((error) => {
          console.warn(
            'Failed to refresh token after community creation:',
            error,
          );
        });
      }

      // After creating in Cerebro, persist optional fields (headline/bio/community_type) in Profile service
      try {
        const { id } = data ?? {};
        const { headline, bio, community_type } = variables ?? {};
        const headlineSafe =
          headline && headline.trim().length ? headline : undefined;
        const bioSafe = bio && bio.trim().length ? bio : undefined;

        if (id && (headlineSafe || bioSafe || community_type)) {
          const updatePayload = {
            communityId: id,
            updateCommunityInput: {
              headline: headlineSafe,
              bio: bioSafe,
              community_type,
            },
          };

          // Use retry with backoff since Profile service may not have synced from Cerebro yet
          await retryOn404(
            () =>
              communitiesApi.updateCommunityCommunitiesCommunityIdPut(
                updatePayload,
              ),
            3, // max 3 retries
            500, // start with 500ms delay (500ms -> 1000ms -> 2000ms)
          );
        } else {
          console.warn(
            ' ==  [useCreateCommunity] Skipping Profile update - condition not met',
          );
        }
      } catch (error) {
        console.error(
          '[useCreateCommunity] Failed to update community details after creation:',
          error,
        );
      }

      await queryClient.invalidateQueries({ queryKey: [communitiesKey] });
      // Invalidate by-id cache as well if available
      try {
        if (data?.id) {
          await queryClient.invalidateQueries({
            queryKey: [communitiesKey, byIdKey, data.id],
          });
        }
      } catch (error) {
        console.warn('Failed to invalidate community cache by id:', error);
      }

      showSnackbar({ text: 'Community created', type: 'success' });
    },
    async onError(error) {
      if (isAxiosError(error) && error.response?.status === 409) {
        showSnackbar({
          text: 'A community with this name or URL already exists. Please choose a different name or URL.',
          type: 'error',
        });
        return;
      }
      showSnackbar({
        text: 'Failed to create community. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useGetCommunityUsers = (communityId?: string) =>
  useAuthQuery({
    queryKey: [communitiesKey, 'users', communityId],
    enabled: Boolean(communityId),
    queryFn: async (): Promise<ListCommunityUserOutput[]> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await cerebroCommunitiesApi.listCommunityUsersByCommunityCommunitiesCommunityIdUsersGet(
          {
            communityId: communityId as string,
          },
        );
      return response.data;
    },
    staleTime: 300000, // 5 minutes
  });

// Memberships (Network): Members with organizer flag
export const useGetCommunityMembers = (
  communityId?: string,
  isActive?: boolean,
) =>
  useAuthQuery({
    queryKey: [communitiesKey, membersKey, communityId, isActive ?? 'any'],
    enabled: Boolean(communityId),
    queryFn: async (): Promise<MemberOutput[]> => {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      const response =
        await membershipsApi.getCommunityMembersMembershipsCommunitiesCommunityIdMembersGet(
          {
            communityId: communityId as string,
            isActive,
          },
        );
      return response.data;
    },
    staleTime: 300000, // 5 minutes
  });

// Cerebro: Create community user with role (owner/editor)
export const useCreateCerebroCommunityUser = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    GetCommunityUserOutput,
    unknown,
    { communityId: string; userId: string; role?: CommunityUserRole }
  >({
    async mutationFn({ communityId, userId, role }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');

      // Create them as a CommunityUser in cerebro with owner/editor role
      const response =
        await cerebroCommunitiesApi.createCommunityUserCommunitiesCommunityIdUsersPost(
          {
            communityId,
            createCommunityUserInput: {
              user_id: userId,
              community_id: communityId,
              role,
            },
          },
        );

      return response.data;
    },
    async onSuccess(_data, { communityId }) {
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, 'users', communityId],
      });
      showSnackbar({
        text: 'Admin user added',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to add admin user',
        type: 'error',
      });
    },
  });
};

// Batch operations: Add community member without snackbar/invalidation (for batch operations)
export const useAddCommunityMemberBatch = () => {
  return useMutation<
    { userId: string },
    unknown,
    { communityId: string; userId: string; isOrganizer?: boolean }
  >({
    async mutationFn({ communityId, userId, isOrganizer }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');

      // 1) Join community via profile service (Network)
      const joinCommunityInput: JoinCommunityInput = {
        community_id: communityId,
        user_id: userId,
      };
      await membershipsApi.joinCommunityMembershipsJoinPost({
        joinCommunityInput,
      });

      // 2) Set organizer status (defaults to false if not specified)
      await membershipsApi.updateMemberOrganizerStatusMembershipsCommunitiesCommunityIdMembersUserIdOrganizerStatusPatch(
        {
          communityId,
          userId,
          updateMemberOrganizerStatusInput: {
            is_organizer: isOrganizer || false,
          },
        },
      );

      return { userId };
    },
  });
};

// Batch operations: Create cerebro community user without snackbar/invalidation (for batch operations)
export const useCreateCerebroCommunityUserBatch = () => {
  return useMutation<
    GetCommunityUserOutput,
    unknown,
    { communityId: string; userId: string; role?: CommunityUserRole }
  >({
    async mutationFn({ communityId, userId, role }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');

      // Create them as a CommunityUser in cerebro with owner/editor role
      const response =
        await cerebroCommunitiesApi.createCommunityUserCommunitiesCommunityIdUsersPost(
          {
            communityId,
            createCommunityUserInput: {
              user_id: userId,
              community_id: communityId,
              role,
            },
          },
        );

      return response.data;
    },
  });
};

// Cerebro: Update community user role
export const useUpdateCommunityUser = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<
    GetCommunityUserOutput,
    unknown,
    { communityId: string; userId: string; role?: CommunityUserRole }
  >({
    async mutationFn({ communityId, userId, role }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');
      assertParameterIsNotEmptyString(userId, 'userId');
      const response =
        await cerebroCommunitiesApi.updateCommunityUserCommunitiesCommunityIdUsersUserIdPut(
          {
            communityId,
            userId,
            updateCommunityUserInput: { role },
          },
        );
      return response.data;
    },
    async onSuccess(_data, { communityId }) {
      await queryClient.invalidateQueries({
        queryKey: [communitiesKey, 'users', communityId],
      });
      showSnackbar({
        text: 'User role updated',
        type: 'success',
      });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to update user role',
        type: 'error',
      });
    },
  });
};

// Cerebro: Delete community user (remove role)
export const useDeleteCommunityUser = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  return useMutation<unknown, unknown, { communityId: string; userId: string }>(
    {
      async mutationFn({ communityId, userId }) {
        assertParameterIsNotEmptyString(communityId, 'communityId');
        assertParameterIsNotEmptyString(userId, 'userId');
        const response =
          await cerebroCommunitiesApi.deleteCommunityUserCommunitiesCommunityIdUsersUserIdDelete(
            {
              communityId,
              userId,
            },
          );
        return response.data;
      },
      async onSuccess(_data, { communityId }) {
        await queryClient.invalidateQueries({
          queryKey: [communitiesKey, 'users', communityId],
        });
        showSnackbar({
          text: 'User role removed',
          type: 'success',
        });
      },
      async onError() {
        showSnackbar({
          text: 'Failed to remove user role',
          type: 'error',
        });
      },
    },
  );
};

// Delete community via Cerebro service (handles cascade deletion)
export const useDeleteCommunity = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<unknown, unknown, { communityId: string }>({
    async mutationFn({ communityId }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');

      // Delete via Cerebro service (handles cascade deletion to other services)
      await cerebroCommunitiesApi.deleteCommunityCommunitiesCommunityIdDelete({
        communityId,
      });

      return { communityId };
    },
    async onSuccess() {
      // Refresh token to update JWT claims (remove deleted community from owned communities)
      await refreshToken().catch((error) => {
        console.warn(
          'Failed to refresh token after community deletion:',
          error,
        );
      });

      // Invalidate all community-related queries
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [communitiesKey] }),
      ]);

      showSnackbar({ text: 'Community deleted', type: 'success' });
    },
    async onError() {
      showSnackbar({
        text: 'Failed to delete community. Please try again.',
        type: 'error',
      });
    },
  });
};

export const useCreateCommunityMeta = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  return useMutation<
    void,
    unknown,
    {
      communityId: string;
      communitySize?: CommunitySize;
      fundingSources?: FundingSource[];
    }
  >({
    async mutationFn({ communityId, communitySize, fundingSources }) {
      assertParameterIsNotEmptyString(communityId, 'communityId');

      const input: CreateCommunityMetaInput = {
        community_size: communitySize,
        funding_source: fundingSources,
      };

      await communitiesApi.createCommunityMetaCommunitiesCommunityIdMetaPost({
        communityId,
        createCommunityMetaInput: input,
      });
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: [communitiesKey] });
    },
    onError() {
      showSnackbar({
        text: 'Failed to save organization details.',
        type: 'error',
      });
    },
  });
};
